import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button } from '../common';
import ProblemPreview from '../ProblemPreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const BulkCopyModal = ({
  isOpen,
  onClose,
  targetTreeId,  // 현재 트리 (복사 대상)
  fetchWithTokenRefresh,
  fetchProblemCounts
}) => {
  const [sourceTreeId, setSourceTreeId] = useState('');
  const [treeList, setTreeList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isBulkCopying, setIsBulkCopying] = useState(false);
  const [sourceTreeInfo, setSourceTreeInfo] = useState(null);
  const [targetTreeInfo, setTargetTreeInfo] = useState(null);
  const [totalCopyAbleCount, setTotalCopyAbleCount] = useState(0);
  const [previewProblemId, setPreviewProblemId] = useState(null);
  const [bulkCopyResult, setBulkCopyResult] = useState(null);

  // 트리 목록 가져오기
  useEffect(() => {
    const fetchTreeList = async () => {
      try {
        const response = await fetchWithTokenRefresh('/api/testset/lists', { method: 'GET' });
        if (response?.status === 'success' && response?.data) {
          // 현재 트리를 제외하고, is_closed가 true인 트리만 표시
          const filteredTrees = response.data.filter(tree => 
            tree.tree_id !== parseInt(targetTreeId) && tree.is_closed === true
          );
          setTreeList(filteredTrees);
        }
      } catch (error) {
        console.error('트리 목록 가져오기 실패:', error);
      }
    };

    if (isOpen) {
      fetchTreeList();
    }
  }, [isOpen, targetTreeId, fetchWithTokenRefresh]);

  // 복사 가능한 문제 검색
  const handleSearch = useCallback(async () => {
    if (!sourceTreeId) {
      alert('원본 트리를 선택해주세요.');
      return;
    }

    setIsLoading(true);
    setBulkCopyResult(null);
    try {
      const response = await fetchWithTokenRefresh(
        `/api/referencefields/bulk-copy?source_tree_id=${sourceTreeId}&target_tree_id=${targetTreeId}`,
        { method: 'GET' }
      );

      if (response?.status === 'success' && response?.data) {
        setSearchResults(response.data.problems || []);
        setSourceTreeInfo(response.data.source_tree_info);
        setTargetTreeInfo(response.data.target_tree_info);
        setTotalCopyAbleCount(response.data.total_copy_able_count || 0);
        setSelectedProblems(new Set(response.data.problems.map(p => p.problem_id)));
      } else if (response?.status === 'error' && response?.status_code === 400) {
        alert('원본 트리와 대상 트리의 구조가 일치하지 않습니다.');
        setSearchResults([]);
        setSourceTreeInfo(null);
        setTargetTreeInfo(null);
        setTotalCopyAbleCount(0);
      } else {
        setSearchResults([]);
        setSourceTreeInfo(null);
        setTargetTreeInfo(null);
        setTotalCopyAbleCount(0);
        alert('복사 가능한 문제가 없습니다.');
      }
    } catch (error) {
      console.error('문제 검색 실패:', error);
      alert('문제 검색에 실패했습니다.');
      setSearchResults([]);
      setSourceTreeInfo(null);
      setTargetTreeInfo(null);
      setTotalCopyAbleCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [sourceTreeId, targetTreeId, fetchWithTokenRefresh]);

  // sourceTreeId 변경 시 자동 검색
  useEffect(() => {
    if (sourceTreeId && isOpen) {
      handleSearch();
    }
  }, [sourceTreeId, isOpen, handleSearch]);

  // 대규모 복사 실행
  const handleBulkCopy = useCallback(async () => {
    const problemIds = Array.from(selectedProblems);
    if (problemIds.length === 0) {
      alert('복사할 문제를 선택해주세요.');
      return;
    }

    setIsBulkCopying(true);
    setBulkCopyResult(null);
    try {
      const response = await fetchWithTokenRefresh(
        '/api/referencefields/bulk-copy',
        {
          method: 'POST',
          body: JSON.stringify({
            source_tree_id: parseInt(sourceTreeId),
            target_tree_id: parseInt(targetTreeId),
            target_problem_ids: problemIds
          })
        }
      );

      if (response?.status === 'success' && response?.data) {
        setBulkCopyResult(response.data);
        
        // 성공한 문제들을 검색 결과에서 제거
        const successList = response.data.problems_list
          ?.filter(p => p.result === 'success')
          ?.map(p => p.problem_id) || [];
        
        if (successList.length > 0) {
          setSearchResults(prev => prev.filter(p => !successList.includes(p.problem_id)));
          setSelectedProblems(new Set());
          // 문제 수 업데이트
          await fetchProblemCounts();
        }

        // 결과 메시지 생성
        let message = '';
        if (response.data.success_count > 0) {
          message += `✅ ${response.data.success_count}개 문제 복사 성공\n`;
        }
        if (response.data.already_added_count > 0) {
          message += `⚠️ ${response.data.already_added_count}개 이미 편성됨\n`;
        }
        if (response.data.source_not_found_count > 0) {
          message += `❌ ${response.data.source_not_found_count}개 원본에서 찾을 수 없음\n`;
        }
        if (response.data.target_address_not_found_count > 0) {
          message += `❌ ${response.data.target_address_not_found_count}개 대상 좌표 없음\n`;
        }
        if (response.data.error_count > 0) {
          message += `❌ ${response.data.error_count}개 오류 발생\n`;
        }

        if (message) {
          alert(message);
        }
      } else {
        alert('대규모 복사에 실패했습니다.');
      }
    } catch (error) {
      console.error('대규모 복사 실패:', error);
      alert('대규모 복사에 실패했습니다.');
    } finally {
      setIsBulkCopying(false);
    }
  }, [selectedProblems, sourceTreeId, targetTreeId, fetchWithTokenRefresh, fetchProblemCounts]);

  // 문제 선택/해제
  const toggleProblemSelection = (problemId) => {
    setSelectedProblems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(problemId)) {
        newSet.delete(problemId);
      } else {
        newSet.add(problemId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectedProblems.size === searchResults.length) {
      setSelectedProblems(new Set());
    } else {
      setSelectedProblems(new Set(searchResults.map(p => p.problem_id)));
    }
  };

  // 라벨 파싱하여 표시
  const formatLabels = (labels) => {
    if (!labels) return '';
    const parts = labels.split(',');
    const displayParts = parts.slice(0, 3).join(', ');
    return parts.length > 3 ? `${displayParts}, ...` : displayParts;
  };

  // 결과 상태별 색상
  const getResultColor = (result) => {
    switch (result) {
      case 'success':
        return 'text-green-600';
      case 'already_added':
        return 'text-orange-600';
      default:
        return 'text-red-600';
    }
  };

  // 결과 상태별 라벨
  const getResultLabel = (result) => {
    switch (result) {
      case 'success':
        return '성공';
      case 'already_added':
        return '이미 존재';
      case 'source_not_found':
        return '원본 없음';
      case 'source_info_not_found':
        return '원본 정보 없음';
      case 'target_address_not_found':
        return '대상 좌표 없음';
      case 'problem_not_found':
        return '문제 없음';
      case 'error':
        return '오류';
      default:
        return result;
    }
  };

  return createPortal(
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="문제 편성 복사"
        size="xl"
        zIndex={9999950}  // ProblemPreview(9999960)보다 낮게 설정
        footer={
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            닫기
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkCopy}
            disabled={isBulkCopying || selectedProblems.size === 0}
          >
            {isBulkCopying ? '복사 중...' : `선택한 ${selectedProblems.size}개 복사`}
          </Button>
        </div>
        }
      >
        <div className="space-y-4">
          {/* 트리 선택 영역 */}
          <div className="flex gap-2">
            <select
              value={sourceTreeId}
              onChange={(e) => setSourceTreeId(e.target.value)}
              className="flex-1 min-w-0 rounded-3xl border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
            >
              <option value="">원본 교육과정 선택</option>
              {treeList.map(tree => (
                <option key={tree.tree_id} value={tree.tree_id}>
                  {tree.edu_ver} - {tree.tree_name}
                </option>
              ))}
            </select>
            <Button
              onClick={handleSearch}
              disabled={isLoading}
              variant="primary"
            >
              {isLoading ? '검색 중...' : '검색'}
            </Button>
          </div>

          {/* 트리 정보 및 복사 가능 문제 수 표시 */}
          {searchResults.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <div className="text-sm text-gray-600">
                복사 가능: {searchResults.length}개 / 전체: {totalCopyAbleCount}개
                {totalCopyAbleCount > 500 && (
                  <span className="ml-2 text-orange-500">
                    (최대 500개만 조회됨)
                  </span>
                )}
              </div>
              {sourceTreeInfo && targetTreeInfo && (
                <div className="text-sm text-gray-700">
                  <div>원본: {sourceTreeInfo.tree_name} ({sourceTreeInfo.source_probs_count}개)</div>
                  <div className="text-right">→ 대상: {targetTreeInfo.tree_name} ({targetTreeInfo.target_probs_count}개)</div>
                </div>
              )}
            </div>
          )}

          {/* 복사 결과 상세 표시 */}
          {bulkCopyResult && bulkCopyResult.problems_list && (
            <div className="rounded-lg bg-blue-50 p-3 max-h-40 overflow-y-auto">
              <div className="font-semibold mb-2">복사 결과 상세:</div>
              <div className="space-y-1 text-xs">
                {bulkCopyResult.problems_list.map((problem) => (
                  <div key={problem.problem_id} className={`${getResultColor(problem.result)}`}>
                    <span className="font-medium">{problem.problem_id}</span>
                    <span className="mx-1">-</span>
                    <span>{getResultLabel(problem.result)}</span>
                    {problem.result === 'success' && (
                      <>
                        <span className="mx-1">:</span>
                        <span>{problem.source_comp_name} → {problem.target_comp_name}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 검색 결과 테이블 */}
          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedProblems.size === searchResults.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">전체 선택</span>
                </label>
                <span className="text-sm text-gray-600">
                  {selectedProblems.size}개 선택됨
                </span>
              </div>

              <div className="max-h-[400px] overflow-y-auto overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-100">
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">선택</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">문제 ID</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">라벨</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 hidden md:table-cell bg-gray-100">작성자</th>
                      <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 hidden md:table-cell bg-gray-100">생성일</th>
                      <th className="px-2 md:px-4 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100">미리보기</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {searchResults.map((problem) => (
                      <tr key={problem.problem_id} className="hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selectedProblems.has(problem.problem_id)}
                            onChange={() => toggleProblemSelection(problem.problem_id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm">{problem.problem_id}</td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600">
                          <span title={problem.labels}>
                            {formatLabels(problem.labels)}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600 hidden md:table-cell">{problem.author}</td>
                        <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600 hidden md:table-cell">
                          {new Date(problem.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-2 md:px-4 py-2 text-center">
                          <button
                            onClick={() => setPreviewProblemId(problem.problem_id)}
                            className="text-blue-500 hover:text-blue-700 text-xs md:text-sm"
                          >
                            보기
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 검색 결과가 없을 때 */}
          {!isLoading && searchResults.length === 0 && sourceTreeId && (
            <div className="text-center py-8 text-gray-500">
              복사 가능한 문제가 없습니다.
            </div>
          )}
        </div>
      </Modal>
      
      {/* 문제 미리보기 모달 - Modal 밖에 위치시켜 z-index 문제 해결 */}
      {previewProblemId && (
        <ProblemPreview
          isOpen={true}
          onClose={() => setPreviewProblemId(null)}
          problemIds={[previewProblemId]}
          showNumbers={false}
          correctList={[]}
        />
      )}
    </>,
    document.body
  );
};

export default BulkCopyModal;
